import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import Paths from 'helper/paths';
import i18next from 'i18next';
import FlightFormInterface from 'models/flight/flightForm.interface';
import FlightPriceResponseInterface from 'models/flight/flightPriceResponse.interface';
import { OriginDestinationOptions } from 'models/flight/flitghtResponse.interface';
import KeyValueInterface from 'models/keyValue.interface';
import { Maybe } from 'models/types';
import { RootState } from '../../app/store';

export interface FlightBookStepInterface {
  step: number;
  title: string;
}
export interface FlightComplementaryFormInterface {
  departureText: string;
  departureCountryCode: string;
  destinationText: string;
  destinationCountryCode: string;
}
export interface FlightState {
  form: Partial<FlightFormInterface>;
  complementaryForm: FlightComplementaryFormInterface;
  bookSteps: Array<FlightBookStepInterface>;
  activeBookStep: number;
  flightList: OriginDestinationOptions[];
  selectedFlight: Maybe<OriginDestinationOptions>;
  selectedFlightPrice: Maybe<FlightPriceResponseInterface>;
}

const initialState: FlightState = {
  form: {
    kind: 'economy',
    adtCount: 1,
    departure: '',
    dateTime: '',
    destination: '',
    returnDateTime: '',
    discountTicketId: '',
  },
  complementaryForm: {
    departureText: '-',
    departureCountryCode: '-',
    destinationText: '-',
    destinationCountryCode: '-',
  },
  bookSteps: [
    {
      step: 0,
      title: '',
    },
    {
      step: 1,
      title: i18next.t('pages.services.flight.pages.book.sections.steps.passengers'),
    },
    {
      step: 2,
      title: i18next.t('pages.services.flight.pages.book.sections.steps.payment'),
    },
    {
      step: 3,
      title: i18next.t('pages.services.flight.pages.book.sections.steps.book'),
    },
  ],
  activeBookStep: 0,
  flightList: [],
  selectedFlight: null,
  selectedFlightPrice: null,
};

// https://staging.ffp.homares.ir/api/v1/users/getAllDepartures
// https://staging.ffp.homares.ir/api/v1/users/getArrivalByDeparture?departure=MED

// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched. Thunks are
// typically used to make async requests.
// export const incrementAsync = createAsyncThunk('counter/fetchCount', async (amount: number) => {
//   const response = await fetchCount(amount);
//   // The value we return becomes the `fulfilled` action payload
//   return response.data;
// });

export const flightSlice = createSlice({
  name: 'flight',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    updateForm: (state, action: PayloadAction<KeyValueInterface<string | number>>) => {
      state.form[action.payload.key] = action.payload.value;
    },
    setForm: (state, action: PayloadAction<Partial<FlightFormInterface>>) => {
      state.form = action.payload;
    },
    updateComplementaryForm: (state, action: PayloadAction<KeyValueInterface<string | number>>) => {
      state.complementaryForm[action.payload.key] = action.payload.value;
    },
    changeFlightBookSteps: (state, action: PayloadAction<number>) => {
      state.activeBookStep = action.payload;
    },
    setFirstFlightBookStepTitle: (state, action: PayloadAction<string>) => {
      state.bookSteps[0].title = action.payload;
    },
    setFlightList: (state, action: PayloadAction<OriginDestinationOptions[]>) => {
      // state.flightList = action.payload.filter((e) =>
      // e.originDestinationOption[0].flightSegment[0].bookingClassAvails.bookingClassAvail.some((el) => {
      //   return ['Z1', 'H1'].includes(el.resBookDesigCode);
      // })
      // );
      state.flightList = action.payload;
    },
    setSelectedFlight: (state, action: PayloadAction<OriginDestinationOptions>) => {
      state.selectedFlight = action.payload;
    },
    setSelectedFlightPrice: (state, action: PayloadAction<FlightPriceResponseInterface>) => {
      state.selectedFlightPrice = action.payload;
    },
  },
});

export const {
  updateForm,
  setForm,
  changeFlightBookSteps,
  setFirstFlightBookStepTitle,
  updateComplementaryForm,
  setFlightList,
  setSelectedFlight,
  setSelectedFlightPrice,
} = flightSlice.actions;

export const flightFormDataState = (state: RootState) => state.flight.form;
export const formDataToQuery = (state: RootState) => Paths.ObjectToQueryParams({ ...state.flight.form });
export const formPassengersDataState = (state: RootState) =>
  state.flight.form
    ? { adult: state.flight.form.adtCount, child: state.flight.form.chdCount, infant: state.flight.form.infCount }
    : { adult: 0, child: 0, infant: 0 };
export const complementaryFormDataState = (state: RootState) => state.flight.complementaryForm;
export const flightBookSteps = (state: RootState) => state.flight.bookSteps;
export const flightActiveBookStep = (state: RootState) => state.flight.activeBookStep;
export const flightList = (state: RootState) => state.flight.flightList;
export const selectedFlightState = (state: RootState) => state.flight.selectedFlight;
export const selectedFlightPriceState = (state: RootState) => state.flight.selectedFlightPrice;

// We can also write thunks by hand, which may contain both sync and async logic.
// Here's an example of conditionally dispatching actions based on current state.
// export const incrementIfOdd =
//   (amount: number): AppThunk =>
//   (dispatch, getState) => {
//     const currentValue = formData(getState());
//     if (currentValue % 2 === 1) {
//       dispatch(incrementByAmount(amount));
//     }
//   };

export default flightSlice.reducer;
